import { computed, onMounted, onUnmounted, ref } from "vue";
import { MemoryMonitor } from "@/utils/MemoryMonitor";
import {
  MEMORY_WARNING_PERCENT,
  MEMORY_WARNING_REARM_PERCENT,
  MEMORY_WARNING_FALLBACK_MB,
  MEMORY_WARNING_REARM_FALLBACK_MB,
} from "@/utils/constants";

// Module-level state shared between the two exports (mirrors useStorageQuota.ts's percentageUsed).
const percentageOfLimit = ref<number | null>(null); // null when jsHeapSizeLimit unavailable (Safari/iOS)
const sessionDeltaMb = ref<number | null>(null); // display value + fallback signal

// Value (percentage or Mo, whichever signal is active) at the moment the user last dismissed
// the banner. null = not dismissed. Deliberately not persisted anywhere (no localStorage) —
// unlike StorageBanner's calendar-day dismissal, this value is only ever meaningful within the
// current tab session (sessionDelta()/percentageOfLimit both reset on a full reload too).
const dismissedAt = ref<number | null>(null);

export const useGetMemoryWarningInfo = () => {
  const usingPercentage = computed(() => percentageOfLimit.value !== null);

  const isMemoryHigh = computed(() => {
    if (usingPercentage.value) {
      return (percentageOfLimit.value as number) >= MEMORY_WARNING_PERCENT;
    }
    return (
      sessionDeltaMb.value !== null &&
      sessionDeltaMb.value >= MEMORY_WARNING_FALLBACK_MB
    );
  });

  // Re-arms as memory keeps climbing: once dismissed, stays hidden only until the current
  // value grows past where it was at dismiss time by the configured margin.
  const showBanner = computed(() => {
    if (!isMemoryHigh.value) return false;
    if (dismissedAt.value === null) return true;
    const current = usingPercentage.value
      ? (percentageOfLimit.value as number)
      : (sessionDeltaMb.value as number);
    const rearmMargin = usingPercentage.value
      ? MEMORY_WARNING_REARM_PERCENT
      : MEMORY_WARNING_REARM_FALLBACK_MB;
    return current >= dismissedAt.value + rearmMargin;
  });

  function dismiss() {
    dismissedAt.value = usingPercentage.value
      ? percentageOfLimit.value
      : sessionDeltaMb.value;
  }

  return {
    percentageOfLimit,
    sessionDeltaMb,
    usingPercentage,
    isMemoryHigh,
    showBanner,
    dismiss,
  };
};

// Le sondage est partagé : AuthLayout et DrawingCanvas (deux arbres de routes
// distincts, cf. router.ts) l'arment chacun de leur côté, et les deux restent
// brièvement montés pendant une transition de route. On compte les abonnés pour
// n'avoir qu'un seul intervalle et ne l'arrêter qu'au dernier démontage.
let subscribers = 0;
let intervalId: ReturnType<typeof setInterval> | null = null;

const checkMemory = () => {
  const snap = MemoryMonitor.snapshot();
  percentageOfLimit.value =
    snap.jsHeapLimit > 0 ? (snap.jsHeap / snap.jsHeapLimit) * 100 : null;
  const delta = MemoryMonitor.sessionDelta();
  sessionDeltaMb.value = delta === null ? null : delta / 1024 / 1024;
};

export const useMemoryWarning = () => {
  onMounted(() => {
    subscribers++;
    if (intervalId === null) {
      intervalId = setInterval(checkMemory, 10 * 1000);
    }
    checkMemory();
  });

  onUnmounted(() => {
    subscribers = Math.max(0, subscribers - 1);
    if (subscribers === 0 && intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  });
};
